import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { v4 as uuid4 } from 'uuid';

import { API_ENDPOINT, API_V2_ENDPOINT } from '../constants';

enum Methods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

const DEFAULT_CONTENT_TYPE = 'application/json';

type ErrorDetail = string | Array<string | { msg?: string }> | null | undefined;

export class FetchWrapperError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'FetchWrapperError';
  }
}

function request(method: Methods) {
  return async (params: any, body?: any, contentType: string = DEFAULT_CONTENT_TYPE) => {
    const authorizationHeader = await authHeader(
      params.url,
      params.getAccessTokenSilently,
    );
    const requestOptions = {
      method,
      headers: { ...authorizationHeader, ...params.headers, 'Idempotency-Key': uuid4() },
      body,
      signal: params.signal,
    };

    if (body && contentType === DEFAULT_CONTENT_TYPE) {
      requestOptions.headers['Content-Type'] = contentType;
      requestOptions.body = JSON.stringify(body);
    }
    const response = await fetch(params.url, requestOptions);
    return handleResponse(response);
  };
}

async function authHeader(url: string, getAccessTokenSilently: any) {
  if (!getAccessTokenSilently) {
    return {};
  }

  // return auth header with jwt if user is logged in and request is to the api url
  try {
    const token = await getAccessTokenSilently();
    const isLoggedIn = !!token;
    const isApiUrl = url.startsWith(API_ENDPOINT) || url.startsWith(API_V2_ENDPOINT);
    if (isLoggedIn && isApiUrl) {
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  } catch {
    return {};
  }
}

function handleResponse(response: any) {
  return response.text().then(async (text: string) => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      const error = parseErrorDetail(data?.detail, response.statusText);
      // Display specific errors
      if ([403, 502, 503, 504].includes(response.status)) {
        toast.error(error, { toastId: 'global-toast-error' });
      }
      // Capture exceptions in sentry
      if ([502, 503, 504].includes(response.status)) {
        Sentry.captureException(new Error(`HTTP Error: ${response.status} ${error}`));
      }
      return Promise.reject(new FetchWrapperError(error, `${response.status}`));
    }
    return data;
  });
}

function parseErrorDetail(detail: ErrorDetail, statusText: string): string {
  if (!Array.isArray(detail)) {
    return String(detail ?? statusText);
  }

  if (detail.length === 0) {
    return 'Unknown Error format';
  }

  const [first] = detail;
  if (Object.prototype.toString.call(first) === '[object String]') {
    return detail.join(', ');
  }

  const firstDetail = Object(first);
  return 'msg' in firstDetail && firstDetail.msg
    ? String(firstDetail.msg)
    : 'Unknown Error format';
}

export const fetchWrapper = {
  get: request(Methods.GET),
  post: request(Methods.POST),
  put: request(Methods.PUT),
  patch: request(Methods.PATCH),
  delete: request(Methods.DELETE),
};
